import React from 'react';
import { connect } from 'react-redux';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Badge from 'react-bootstrap/Badge';
import Alert from 'react-bootstrap/Alert';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';
import { patientSingleFetch, patientSingleUnload } from '../../lib/redux/actions/patients';
import { patientNotesFetch, patientNotesUnload } from '../../lib/redux/actions/patientNotes';
import MASpinner from '../../components/common/MASpinner';
import * as ROUTES from '../../lib/routing/frontend';
import { patientDisplayName } from '../../lib/helper/patientFilters';
import { INVOICES_UNPAID, filterPatientInvoices } from '../../lib/helper/patientInvoices';
import PatientIdentityCard from './components/PatientIdentityCard';
import PatientConsultantsCard from './components/PatientConsultantsCard';
import PatientInvoicesCard from './components/PatientInvoicesCard';
import PatientNotes from '../../components/Patient/PatientNotes';
import CreatePatientNoteForm from '../../components/forms/Patient/CreatePatientNoteForm';

const mapStateToProps = (state) => ({
  ...state.patients,
});

const mapDispatchToProps = {
  patientSingleFetch,
  patientSingleUnload,
  patientNotesFetch,
  patientNotesUnload,
};

const PATIENTS_INDEX = ROUTES.PATIENTS.ALL.replace('/:page?', '');

/**
 * MED-921 - one person, and everything the register holds about them.
 *
 * Framed the way ConsultantSingle frames a consultant: one white card, a back link in the header
 * with a status on the right, a title, and `crud__section` blocks inside. The two screens answer
 * the same shape of question, so they should not look like different applications.
 *
 * Identity is read-only. Notes (MED-922) are the one thing written from here. Contact editing is
 * slice 3 and the noShortfall toggle is slice 2 - neither is stubbed in, because a control that
 * does nothing is worse than an absent one on a screen about patient identity.
 */
class PatientSingleContainer extends React.Component {
  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.load();
    }
  }

  componentWillUnmount() {
    this.props.patientSingleUnload();
    this.props.patientNotesUnload();
  }

  load() {
    const { id } = this.props.match.params;
    this.props.patientSingleFetch(id);
    // Notes are a separate fetch, through the same subresource the invoice screen uses, so the
    // patient read stays as light for 57,624 people as it was before notes existed.
    this.props.patientNotesFetch(id);
  }

  render() {
    const { patient, isFetchingPatient, hasLoadedPatient } = this.props;

    if (isFetchingPatient) {
      return (
        <Card>
          <Card.Body>
            <MASpinner />
          </Card.Body>
        </Card>
      );
    }

    // A refusal and a genuinely missing patient both land here. Consultant-side users never reach
    // this route - authByPath sends them to /denied first - so what this covers in practice is a
    // hand-typed or stale id.
    if (!hasLoadedPatient || !patient) {
      return (
        <Card>
          <Card.Body>
            <Alert variant={'warning'} className="mb-0">
              <i className="fas fa-exclamation-triangle" /> That patient could not be loaded. <Link to={PATIENTS_INDEX}>Back to all patients</Link>
            </Alert>
          </Card.Body>
        </Card>
      );
    }

    const name = patientDisplayName(patient);
    const unpaidCount = filterPatientInvoices(patient.invoices, INVOICES_UNPAID).length;

    return (
      <div>
        <Helmet>
          <title>{name} - A Patient</title>
        </Helmet>
        <Row>
          <Col>
            <Card className="mb-4">
              <Card.Header className={'card-header--crud'}>
                <div className="d-flex align-items-center">
                  <span className="crud__meta">
                    <Link to={PATIENTS_INDEX}>&larr; Back to Patients</Link>
                  </span>
                  {/*
                    The same slot the consultant screen uses for active/inactive. What matters
                    about a patient at a glance is whether anything is still owed, so that is what
                    goes here.
                  */}
                  <span className="ml-auto crud__statuses">
                    {unpaidCount > 0 ? (
                      <Badge pill variant={'danger'} className="ml-2 text-uppercase">
                        <i className="fas fa-exclamation-circle" /> {unpaidCount} unpaid
                      </Badge>
                    ) : (
                      <Badge pill variant={'primary'} className="ml-2 text-uppercase">
                        Nothing outstanding
                      </Badge>
                    )}
                  </span>
                </div>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col>
                    <h1 className="crud__body-title">Patient: {name}</h1>
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col md={4}>
                    <PatientIdentityCard patient={patient} />
                    <PatientConsultantsCard patient={patient} />
                  </Col>
                  <Col md={8}>
                    <PatientInvoicesCard patient={patient} />
                    <PatientNotes patientId={patient.id} />
                    <CreatePatientNoteForm patientId={patient.id} form={`CreatePatientNoteForm_${patient.id}`} />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PatientSingleContainer);
